import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { dataCollectionParser } from '../lib/response/dataParser';
import { compare as jsonPatchCompare } from 'fast-json-patch';
import { cloneDeep } from 'lodash';
import {
  Enums,
  ManifestService,
  MmsManifestService
} from '@mobiz-sa/smart-renderer';
import Vue from 'vue';

function getState() {
  return {
    id: undefined,
    manifest: undefined,
    defaultFields: undefined,
    updatedAt: undefined,
    manifestShadow: undefined,
    selectedContentId: undefined,
    shopify: undefined,
    sendImageAsIs: undefined
  };
}
export const state = getState();

export const getters = {
  getImageContent(state): any {
    return (
      state.manifest?.content &&
      MmsManifestService.getImageContent(state.manifest)
    );
  },
  getTextContent(state): any {
    return (
      state.manifest?.content &&
      MmsManifestService.getTextContent(state.manifest)
    );
  },
  getSelectedContent(state) {
    if (state.selectedContentId) {
      return state.manifest?.content[state.selectedContentId];
    }
  },
  hasMmsImage(state) {
    return !!state.manifest;
  }
};

export const mutations = {
  clearMmsTemplateStore(state: any) {
    const newState = getState();
    Object.keys(state).forEach(key => {
      state[key] = newState[key];
    });
  },
  removeContent(state: any, { id }): void {
    delete state.manifest.content[id];
    state.manifest = { ...state.manifest };
  },
  setMmsTemplate(state: any, { template }): void {
    state.id = template.id;
    state.manifest = cloneDeep(template.manifest);
    state.updatedAt = template.updatedAt;
    state.manifestShadow = cloneDeep(template.manifest);
    state.defaultFields = cloneDeep(template.defaultFields);
    state.shopify = cloneDeep(template.shopify);
    state.sendImageAsIs = template.sendImageAsIs;
  },

  setManifest(state: any, { manifest }) {
    state.manifest = cloneDeep(manifest);
  },

  setManifestShadow(state: any, { shadowManifest }) {
    state.manifestShadow = cloneDeep(shadowManifest);
  },

  setMmsSettings(state: any, { settings }): void {
    state.manifest.settings = {
      ...state.manifest.settings,
      ...cloneDeep(settings)
    };
  },

  setUpdatedAt(state: any, { updatedAt }): void {
    state.updatedAt = updatedAt;
  },

  setDefaultFields(state: any, { defaultFields }): void {
    state.defaultFields = cloneDeep(defaultFields);
  },

  setSelectedContentId(state: any, { selectedContentId }): void {
    state.selectedContentId = selectedContentId;
  },

  setContent(state: any, { content, resetContent }) {
    const currentContent = resetContent ? {} : state.manifest.content;
    state.manifest = {
      ...state.manifest,
      content: {
        ...currentContent,
        [content.id]: {
          ...state.manifest.content?.[content.id],
          ...cloneDeep(content)
        }
      }
    };
    Vue.set(state.manifest.content, content.id, {
      ...state.manifest.content?.[content.id],
      ...cloneDeep(content)
    });
  },

  setShopify(state: any, { shopify }) {
    state.shopify = shopify
      ? {
          ...shopify
        }
      : shopify;
  },

  setSendImageAsIs(state: any, { sendImageAsIs }) {
    state.sendImageAsIs = sendImageAsIs;
  }
};

export const actions = {
  async createMmsTemplate(
    { state, commit },
    { manifest, isDraft }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/mms-templates-create',
        {
          data: {
            attributes: {
              manifest,
              sendImageAsIs: state.sendImageAsIs
            }
          }
        },
        {
          params: {
            isDraft
          }
        }
      );
      commit('setMmsTemplate', {
        template: response.data.data.attributes
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async deleteMmsTemplate({ commit }, { id }): Promise<ActionResponse> {
    try {
      const response = await createAxios().delete(
        '/backend/mms-templates-delete',
        {
          params: {
            id
          }
        }
      );
      commit('setMmsTemplate', {
        template: {}
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: undefined
      };
    }
  },

  async updateMmsTemplate({ state, commit }): Promise<ActionResponse> {
    try {
      const manifestPatch = jsonPatchCompare(
        state.manifestShadow,
        state.manifest
      );

      const response = await createAxios().patch(
        '/backend/mms-templates-update',
        {
          data: {
            type: 'mmsTemplates',
            attributes: {
              manifestPatch,
              defaultFields: state.defaultFields,
              shopify: state.shopify,
              sendImageAsIs: state.sendImageAsIs
            }
          }
        },
        {
          params: {
            id: state.id
          }
        }
      );
      commit('setUpdatedAt', {
        updatedAt: response.data.data.attributes.updatedAt
      });
      commit('setManifestShadow', {
        shadowManifest: response.data.data.attributes?.manifest
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadMmsTemplate({ commit }, { id }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get('/backend/mms-templates-show', {
        params: {
          id
        }
      });
      commit('setMmsTemplate', {
        template: response.data.data.attributes
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
